import { CSSObject } from '@emotion/react'
import {
  ColorPrimitives,
  DeprecatedGemColors,
  GemColors,
  gemVar,
  Overlay,
  Theme,
  ThemedColors,
} from '@opallabs/gem'

/**
 * Color variants defined in Gem design system for Buttons and
 * other elements with Hover and Active states.
 */
export type Variants =
  | 'ghost'
  | 'ghostBlue'
  | 'ghostGem'
  | 'primary'
  | 'primaryDestructive'
  | 'primaryGem'
  | 'secondary'
  | 'secondaryDestructive'
  | 'secondaryGem'
  | 'tertiary'

type VariantTheme = {
  /** Color of the border in the active state */
  activeBorder: CSSObject['borderColor']
  /** Overlay applied to baseBackground when element is active or toggled */
  activeOverlay: CSSObject['backgroundColor']
  /** Base background color */
  baseBackground: CSSObject['backgroundColor']
  /** Color of the border in the base state */
  baseBorder: CSSObject['borderColor']
  /** Color applied to text and other foreground elements */
  foreground: CSSObject['color']
  /** Color of the border in the hover state */
  hoverBorder: CSSObject['borderColor']
  /** Overlay applied to baseBackground when element is hovered */
  hoverOverlay: CSSObject['backgroundColor']
}

export const variants: Record<Variants, Record<Theme, VariantTheme>> = {
  primary: {
    light: {
      activeBorder: GemColors.B450,
      activeOverlay: Overlay.Black['24'],
      baseBackground: GemColors.B450,
      baseBorder: GemColors.B450,
      foreground: GemColors.N000,
      hoverBorder: GemColors.B450,
      hoverOverlay: Overlay.Black['12'],
    },
    dark: {
      activeBorder: GemColors.B400,
      activeOverlay: Overlay.White['24'],
      baseBackground: GemColors.B400,
      baseBorder: GemColors.B400,
      foreground: DeprecatedGemColors.DMBLACK,
      hoverBorder: GemColors.B400,
      hoverOverlay: Overlay.White['12'],
    },
  },
  secondary: {
    light: {
      activeBorder: GemColors.B250,
      activeOverlay: Overlay.White['72'],
      baseBackground: GemColors.B150,
      baseBorder: GemColors.B250,
      foreground: DeprecatedGemColors.LMBLUE,
      hoverBorder: GemColors.B250,
      hoverOverlay: Overlay.White['48'],
    },
    dark: {
      activeBorder: DeprecatedGemColors.DMBLUE,
      activeOverlay: Overlay.Black['72'],
      baseBackground: DeprecatedGemColors.BD000,
      baseBorder: DeprecatedGemColors.DMBLUE,
      foreground: DeprecatedGemColors.DMBLUE,
      hoverBorder: DeprecatedGemColors.DMBLUE,
      hoverOverlay: Overlay.Black['48'],
    },
  },
  tertiary: {
    light: {
      activeBorder: GemColors.N150,
      activeOverlay: Overlay.BL200['16'],
      baseBackground: GemColors.N000,
      baseBorder: GemColors.N150,
      foreground: DeprecatedGemColors.LMBLACK,
      hoverBorder: GemColors.N150,
      hoverOverlay: Overlay.BL200['08'],
    },
    dark: {
      activeBorder: GemColors.N600,
      activeOverlay: Overlay.BD200['24'],
      baseBackground: GemColors.N900,
      baseBorder: GemColors.N600,
      foreground: GemColors.N000,
      hoverBorder: GemColors.N600,
      hoverOverlay: Overlay.BD200['16'],
    },
  },
  ghost: {
    light: {
      activeBorder: 'transparent',
      activeOverlay: Overlay.BL200['16'],
      baseBackground: 'transparent',
      baseBorder: 'transparent',
      foreground: DeprecatedGemColors.LMBLACK,
      hoverBorder: 'transparent',
      hoverOverlay: Overlay.BL200['08'],
    },
    dark: {
      activeBorder: 'transparent',
      activeOverlay: Overlay.BD200['24'],
      baseBackground: 'transparent',
      baseBorder: 'transparent',
      foreground: GemColors.N000,
      hoverBorder: 'transparent',
      hoverOverlay: Overlay.BD200['16'],
    },
  },
  ghostBlue: {
    light: {
      activeBorder: DeprecatedGemColors.LMBLUE,
      activeOverlay: Overlay.White['72'],
      baseBackground: 'transparent',
      baseBorder: 'transparent',
      foreground: DeprecatedGemColors.LMBLUE,
      hoverBorder: DeprecatedGemColors.LMBLUE,
      hoverOverlay: Overlay.White['48'],
    },
    dark: {
      activeBorder: DeprecatedGemColors.DMBLUE,
      activeOverlay: Overlay.Black['72'],
      baseBackground: 'transparent',
      baseBorder: 'transparent',
      foreground: DeprecatedGemColors.DMBLUE,
      hoverBorder: DeprecatedGemColors.DMBLUE,
      hoverOverlay: Overlay.Black['48'],
    },
  },
  primaryDestructive: {
    light: {
      activeBorder: GemColors.R400,
      activeOverlay: Overlay.Black['24'],
      baseBackground: GemColors.R400,
      baseBorder: GemColors.R400,
      foreground: GemColors.N000,
      hoverBorder: GemColors.R400,
      hoverOverlay: Overlay.Black['12'],
    },
    dark: {
      activeBorder: GemColors.R300,
      activeOverlay: Overlay.White['24'],
      baseBackground: GemColors.R300,
      baseBorder: GemColors.R300,
      foreground: DeprecatedGemColors.DMBLACK,
      hoverBorder: GemColors.R300,
      hoverOverlay: Overlay.White['12'],
    },
  },
  secondaryDestructive: {
    light: {
      activeBorder: DeprecatedGemColors.LMRED,
      activeOverlay: Overlay.White['72'],
      baseBackground: GemColors.B150,
      baseBorder: DeprecatedGemColors.LMRED,
      foreground: DeprecatedGemColors.LMRED,
      hoverBorder: DeprecatedGemColors.LMRED,
      hoverOverlay: Overlay.White['48'],
    },
    dark: {
      activeBorder: DeprecatedGemColors.DMRED,
      activeOverlay: Overlay.Black['72'],
      baseBackground: DeprecatedGemColors.BD000,
      baseBorder: DeprecatedGemColors.DMRED,
      foreground: DeprecatedGemColors.DMRED,
      hoverBorder: DeprecatedGemColors.DMRED,
      hoverOverlay: Overlay.Black['48'],
    },
  },
  primaryGem: {
    light: {
      activeBorder: gemVar(ThemedColors.GemPrimary2),
      activeOverlay: Overlay.Black['24'],
      baseBackground: gemVar(ThemedColors.GemPrimary4),
      baseBorder: gemVar(ThemedColors.GemPrimary4),
      foreground: gemVar(ColorPrimitives.N000),
      hoverBorder: gemVar(ThemedColors.GemPrimary3),
      hoverOverlay: Overlay.Black['12'],
    },
    dark: {
      activeBorder: gemVar(ThemedColors.GemPrimary2),
      activeOverlay: Overlay.Black['24'],
      baseBackground: gemVar(ThemedColors.GemPrimary4),
      baseBorder: gemVar(ThemedColors.GemPrimary4),
      foreground: gemVar(ColorPrimitives.N000),
      hoverBorder: gemVar(ThemedColors.GemPrimary3),
      hoverOverlay: Overlay.Black['12'],
    },
  },
  secondaryGem: {
    light: {
      activeBorder: gemVar(ThemedColors.GemPrimary4),
      activeOverlay: Overlay.P100['72'],
      baseBackground: gemVar(ThemedColors.GemPrimary0),
      baseBorder: gemVar(ThemedColors.GemPrimary2),
      foreground: gemVar(ThemedColors.GemPrimary6),
      hoverBorder: gemVar(ThemedColors.GemPrimary2),
      hoverOverlay: Overlay.P100['24'],
    },
    dark: {
      activeBorder: gemVar(ThemedColors.GemPrimary4),
      activeOverlay: Overlay.P700['72'],
      baseBackground: gemVar(ThemedColors.GemPrimary0),
      baseBorder: gemVar(ThemedColors.GemPrimary2),
      foreground: gemVar(ThemedColors.GemPrimary6),
      hoverBorder: gemVar(ThemedColors.GemPrimary2),
      hoverOverlay: Overlay.P700['24'],
    },
  },
  ghostGem: {
    light: {
      activeBorder: gemVar(ThemedColors.GemPrimary1),
      activeOverlay: Overlay.P100['72'],
      baseBackground: 'transparent',
      baseBorder: 'transparent',
      foreground: gemVar(ThemedColors.TextPrimary),
      hoverBorder: 'transparent',
      hoverOverlay: Overlay.Black['24'],
    },
    dark: {
      activeBorder: gemVar(ThemedColors.GemPrimary1),
      activeOverlay: Overlay.P700['72'],
      baseBackground: 'transparent',
      baseBorder: 'transparent',
      foreground: gemVar(ThemedColors.TextPrimary),
      hoverBorder: 'transparent',
      hoverOverlay: Overlay.White['12'],
    },
  },
} as const
